import { 
  LoginCredentials, 
  AuthResponse, 
  RefreshResponse,
  ApiResponse,
  AuthUser
} from '../types'
import { apiClient } from './api'

export interface AttestationData {
  first_name: string
  last_name: string
  email: string
  is_attested: boolean
}

class AuthService {
  private token: string | null = null
  private user: AuthUser | null = null
  private api = apiClient

  constructor() {
    this.loadFromStorage()
  }

  private loadFromStorage() {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')

    if (storedToken && storedUser) {
      this.token = storedToken
      this.user = JSON.parse(storedUser)
    }
  }

  private saveToStorage(token: string, user: AuthUser) {
    localStorage.setItem('token', token)
    localStorage.setItem('user', JSON.stringify(user))
    this.token = token
    this.user = user
  }

  private clearStorage() {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    this.token = null
    this.user = null
  }

  getToken(): string | null {
    return this.token
  }

  getUser(): AuthUser | null {
    return this.user
  }

  isAuthenticated(): boolean {
    return !!this.token && !!this.user
  }

  async login(credentials: LoginCredentials): Promise<AuthUser> {
    try {
      const response = await this.api.post<AuthResponse>('/user/auth', credentials)
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'No data received from login endpoint')
      }

      const { token, user } = response.data
      this.saveToStorage(token, user)
      
      return user
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  async signup(userData: Partial<AuthUser>): Promise<AuthUser> {
    try {
      const response = await this.api.post<AuthResponse>('/user/signup', userData)
      console.log('Signup API Response:', response.data)
      
      if (!response.data) {
        throw new Error('No data received from signup endpoint')
      }
      
      const { token, user } = response.data
      
      // For signup, we need to login after successful registration
      this.saveToStorage(token, user)
      
      return user
    } catch (error) {
      console.error('Signup failed:', error)
      throw error
    }
  }

  async refreshToken(): Promise<void> {
    try {
      const response = await this.api.post<RefreshResponse>('/user/refresh')
      
      if (!response.success || !response.data) {
        throw new Error('No data received from refresh token endpoint')
      }
      
      const { token, user } = response.data
      
      // Update only the token, keep the rest of the user data
      if (this.user) {
        this.saveToStorage(token, { ...this.user, ...user })
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      this.logout()
      throw error
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    try {
      const changePasswordData = {
        old_password: oldPassword,
        new_password: newPassword
      }
      
      const response = await this.api.post<ApiResponse<{ message: string }>>('/user/change-password', changePasswordData)
      
      if (!response.success || !response.data) {
        throw new Error('No data received from change password endpoint')
      }
    } catch (error) {
      console.error('Password change failed:', error)
      throw error
    }
  }

  async updateProfile(updates: Partial<AuthUser>): Promise<AuthUser> {
    try {
      const response = await this.api.put<AuthResponse>('/user/profile', updates)
      
      if (!response.success || !response.data) {
        throw new Error('No data received from update profile endpoint')
      }
      
      const { token, user } = response.data
      this.saveToStorage(token, user)
      
      return user
    } catch (error) {
      console.error('Profile update failed:', error)
      throw error
    }
  }

  async validateSession(): Promise<boolean> {
    try {
      if (!this.token) {
        return false
      }

      // Use /user/refresh to check session
      const response = await this.api.post<RefreshResponse>('/user/refresh')
      
      if (response.success && response.data) {
        const { token, user } = response.data
        this.saveToStorage(token, { ...this.user, ...user })
        return true
      }
      
      return false
    } catch (error) {
      console.error('Session validation failed:', error)
      this.clearStorage() // Clear storage on error
      return false
    }
  }

  async requestOtpCode(email: string): Promise<void> {
    try {
      const response = await this.api.post<ApiResponse<{ message: string }>>('/user/otp-login/request', { email })
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to send OTP code')
      }
    } catch (error) {
      console.error('OTP request failed:', error)
      throw error
    }
  }

  async confirmOtpCode(email: string, otp: string): Promise<AuthUser> {
    try {
      const response = await this.api.post<AuthResponse>('/user/otp-login/confirm', { email, otp })
      
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Invalid OTP code')
      }

      const { token, user } = response.data
      this.saveToStorage(token, user)
      
      return user
    } catch (error) {
      console.error('OTP confirmation failed:', error)
      throw error
    }
  }

  logout() {
    this.clearStorage()
  }
}

export const authService = new AuthService() 